import { forwardRef, type HTMLAttributes, type ReactNode } from "react";
import { cn } from "@/lib/cn";
import { toSeverityTone, toStateTone, type Tone, type StateTone } from "@/lib/tone";

// Phase 2.1 — the single severity/status rendering.
//
// Before this, the same "High" could arrive as a .badge-high class, a
// ui/Badge <SeverityBadge>, a <SeverityLabel>, a .tone-chip, a <Chip
// variant="warning">, or an inline hex from severityColor() — six renderings
// drawing on two different colour sets, so one alert looked different
// depending on which page you opened. All of them collapse here.
//
// Colour is NOT chosen in JS. The component names a tone; globals.css's
// [data-tone] rules own every value that tone can resolve to (config.css's
// --hue-* for severity, --state-* for workflow state). That is what keeps a
// hue change a one-line edit, and what stops a call site inventing a seventh
// palette.
//
// Both badges always render their label as text. Severity is never colour
// alone: the ramp cannot separate red/orange/amber for a dichromat (see
// config.css), so the word carries the meaning and the colour reinforces it.

export type ToneBadgeShape = "label" | "chip";
export type { Tone, StateTone };

interface ToneBadgeBaseProps extends Omit<HTMLAttributes<HTMLSpanElement>, "children"> {
  /** `label` (default) is the quiet dot-plus-word for table cells; `chip` is the tinted pill. */
  shape?: ToneBadgeShape;
  /** Overrides the displayed text. Defaults to the value itself, so a badge always reads as words. */
  children?: ReactNode;
}

export interface SeverityBadgeProps extends ToneBadgeBaseProps {
  /** Any severity spelling the API uses — "Critical", "sev1", "informational". */
  value: string | null | undefined;
}

export const SeverityBadge = forwardRef<HTMLSpanElement, SeverityBadgeProps>(function SeverityBadge(
  { value, shape = "label", className, children, ...rest },
  ref
) {
  const tone = toSeverityTone(value);
  return (
    <span
      ref={ref}
      data-tone={tone}
      data-shape={shape}
      // Critical is the one rank drawn as a solid fill, so severity survives
      // being read by someone who cannot separate the hues.
      data-emphasis={tone === "critical" ? "solid" : undefined}
      className={cn("rc-tonebadge", className)}
      {...rest}
    >
      {shape === "label" ? <span className="rc-tonebadge__dot" aria-hidden="true" /> : null}
      {children ?? value ?? "None"}
    </span>
  );
});

export interface StatusBadgeProps extends ToneBadgeBaseProps {
  /** Any status spelling the API uses — "New", "In Progress", "FalsePositive". */
  value: string | null | undefined;
}

export const StatusBadge = forwardRef<HTMLSpanElement, StatusBadgeProps>(function StatusBadge(
  { value, shape = "label", className, children, ...rest },
  ref
) {
  return (
    <span
      ref={ref}
      data-tone={toStateTone(value)}
      data-shape={shape}
      className={cn("rc-tonebadge", className)}
      {...rest}
    >
      {shape === "label" ? <span className="rc-tonebadge__dot" aria-hidden="true" /> : null}
      {children ?? value ?? "Unknown"}
    </span>
  );
});
